import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom'
import stylesheet from './questionnaire.stylesheet';
import { PatientService } from '../../services/patient.service';
import GenericQuestionnaireComponent from './widgets/generic-questionnaire.component';
import {
  CardContent, Button, CircularProgress, DialogActions, DialogContentText, DialogContent, Box, Grid,
  AppBar, Tabs, Tab, Tooltip, DialogTitle, Typography
} from '@mui/material';
import { ClassNameMap, withStyles } from '@mui/styles';
import { toast } from 'react-toastify';
import Helper from '../../utils/helper';
import DialogTitleHeaderComponent from '../patients/detail/common/dialog-title-header.component';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import moment from 'moment';

interface IProps {
  data?: any,
  qu: any,
  pId: string,
  qId: string,
  is_recommended?: boolean,
  isAllQuestionnarie?: boolean,
  handleChange?: any,
  handleIsSubmit: any,
  classes: ClassNameMap,
  handleClose: any,
  handleSave: any,
  isSubmit: any,
  isConfirmSubmit?: boolean
  needsConfirmation: any
  onOptionsLoaded?: (options: string[]) => void // NEW: Callback to pass options to parent
  overrideAnswer?: any // Local answer from QuickEdit, merged with API response
  presetIntent?: 'confirm' | 'deny' // When opening a not-yet-saved chip, fetch the backend
                                     // default answer set so propagated values show immediately
}

const QuestionnaireComponent: React.FC<IProps> = ({
  data,
  qu = '',
  pId = '',
  qId = '',
  is_recommended,
  isAllQuestionnarie = false,
  handleChange,
  handleIsSubmit,
  classes,
  handleClose,
  handleSave,
  isSubmit,
  isConfirmSubmit = false,
  needsConfirmation,
  onOptionsLoaded, // NEW: Receive callback prop
  overrideAnswer,
  presetIntent
}) => {
  const urlParams: any = useParams();
  const patientId: string = urlParams.patientId ?? pId;
  const questionnaireId: string = urlParams.questionnaireId ?? qId;
  const patientService = useMemo(() => new PatientService(), []);
  const recommended_calculators = useSelector((state: RootState) => state.commonData.recommended_calculators)
  const [answerArray, setAnswerArray] = useState([] as any);
  const [hisAnswerArray, setHisAnswerArray] = useState([] as any);
  const [question, setQuestionnaire] = useState([] as any);
  const [showCircularProgress, setCircularProgress] = useState(true)
  const [dataReceivedFlag, setDataReceivedFlag] = useState(false)
  let [answer, setAnswer] = useState({} as any)
  const [currTabIndex, setCurrTabIndex] = useState(0 as number)
  const [configItem, setConfigItem] = useState({
    focusTabIndex: 0,
    tabs: [{ title: 'Current', index: 0, comp: '', props: {} }]
  })

  // Guard to prevent duplicate API calls within a single mount lifecycle
  const hasFetchedRef = useRef(false)
  // Refs to hold current answerArray and overrideAnswer so async callbacks (setInitialAnswers)
  // always read the latest values, not the stale mount-time closure values.
  const answerArrayRef = useRef<any[]>([])
  useEffect(() => { answerArrayRef.current = answerArray }, [answerArray])
  const overrideAnswerRef = useRef<any>(undefined)
  useEffect(() => { overrideAnswerRef.current = overrideAnswer }, [overrideAnswer])

  useEffect(() => {
    if (hasFetchedRef.current) return
    hasFetchedRef.current = true
    const controller = new AbortController()
    fetchQuestionnaireDataset(controller.signal)
    return () => { controller.abort() }
  }, [])

  // Update displayed answers when a user-edit override arrives (without remounting).
  useEffect(() => {
    if (!overrideAnswer || Object.keys(overrideAnswer).length === 0) return
    let changed = false
    const newAnswerArray = [...answerArray]
    Object.entries(overrideAnswer).forEach(([key, val]: [string, any]) => {
      const idx = newAnswerArray.findIndex((el: any) => Object.keys(el)[0] === key)
      if (idx !== -1) {
        if (newAnswerArray[idx][key]?.value !== val?.value) {
          newAnswerArray[idx] = { [key]: val }
          changed = true
        }
      } else {
        newAnswerArray.push({ [key]: val })
        changed = true
      }
    }) 
    if (changed) {
      setAnswerArray(newAnswerArray)
      if (isAllQuestionnarie) {
        const currPayload: any = {}
        newAnswerArray.forEach((obj: any) => {
          const k = Object.keys(obj)[0]
          currPayload[k] = Object.values(obj)[0]
        })
        handleChange(qu, currPayload, false)
      }
    }
  }, [overrideAnswer])

  useEffect(() => {
    if (isConfirmSubmit && !(answerArray.length === 0 || showCircularProgress))
      handleSubmit("confirm")
  }, [isConfirmSubmit])

  const setInitialAnswers = (inputAnswerSet: any) => {
    // Use refs so this async callback always reads the latest values, not mount-time stale closure
    const newAnswers = [...answerArrayRef.current]
    for (let i = 0; i < Object.keys(inputAnswerSet).length; i++) {
      const key = Object.keys(inputAnswerSet)[i]
      const formObj = { [key]: Object.values(inputAnswerSet)[i] }
      // Use exact key match (not truthy check) so falsy values are handled correctly
      const existingIdx = newAnswers.findIndex((el: any) => Object.keys(el)[0] === key)
      if (existingIdx === -1) {
        newAnswers.push(formObj)
      } else if (data?.isBulkEdit) {
        // In bulk edit, prefer a user-edited override over the stale backend value
        if (overrideAnswerRef.current && overrideAnswerRef.current[key] !== undefined) {
          newAnswers[existingIdx] = { [key]: overrideAnswerRef.current[key] }
        } else {
          newAnswers[existingIdx] = formObj
        }
      }
    }
    setAnswerArray(newAnswers)

    if (isAllQuestionnarie || data?.isBulkEdit) {
      const currPayload: any = {}
      newAnswers.forEach((obj: any) => {
        const key = Object.keys(obj)[0]
        currPayload[key] = Object.values(obj)[0]
      })
      // Only propagate when there are actual answers — an empty payload would overwrite
      // a chip-toggle value already stored in queObj for new (never-answered) questionnaires.
      if (Object.keys(currPayload).length > 0) {
        handleChange(qu, currPayload, true)
      }
    }
  }

  const handleTabChange = (event: any, newValue: React.SetStateAction<number>) => setCurrTabIndex(newValue)

  const a11yProps = (index: number) => {
    return {
      id: `scrollable-auto-tab-${index}`,
      'aria-controls': `scrollable-auto-tabpanel-${index}`,
    }
  }

  const getTabTooltip = (tab: any) => {
    let tooltip = tab.title
    if (tab.modified_by) {
      const date = tab.modified_on ? moment.unix(tab.modified_on).format("MMM DD, YYYY h:mm a") : ''
      tooltip += `\nLast saved by ${tab.modified_by}${date ? ` on ${date}` : ''}`
    }
    return tooltip
  }

  const fetchQuestionnaireDataset = (signal?: AbortSignal) => {
    patientService
      .getQuestionnaireDataset(questionnaireId, patientId, signal, presetIntent)
      .then((response: any) => {
        if (response === undefined) return  // aborted
        if (response !== undefined && Object.keys(response?.question_set?.questions)?.length > 0) {
          setQuestionnaire(response?.question_set?.questions || []);

          // NEW: Extract options from the first question and pass to parent
          if (response?.question_set?.questions?.length > 0 && onOptionsLoaded) {
            const firstQuestion = response.question_set.questions[0]
            if (firstQuestion.options && Array.isArray(firstQuestion.options)) {
              onOptionsLoaded(firstQuestion.options)
            }
          }

          // In bulk edit, overrideAnswer carries only user-edited values (not chip-toggle
          // defaults), so it is safe to merge — the user's edit takes priority over backend.
          let answerSet = (response && response?.answer_set) || {}
          if (overrideAnswer && Object.keys(overrideAnswer).length > 0) {
            answerSet = { ...answerSet, ...overrideAnswer }
          }
          setInitialAnswers(answerSet);
          setHisAnswerArray((response && response?.historical_answer_set) || {})
          // Update "Current" tab with modified_by info from response
          let tabs = [{
            ...configItem?.tabs[0],
            modified_by: response?.response_modified_by,
            modified_on: response?.response_modified_on
          }]
          Object.keys(response?.historical_answer_set)
            ?.sort((a: any, b: any) => a.title < b.title ? 1 : -1)
            ?.map((i: any, index: any) => {
              const answerData = response?.historical_answer_set[i]
              tabs = [...tabs, {
                title: i,
                index: (index + 1),
                comp: '',
                props: {},
                modified_by: answerData?.modified_by,
                modified_on: answerData?.modified_on
              }]
            })
          setConfigItem({ ...configItem, tabs: tabs })
        } else {
          setQuestionnaire([]);
          setInitialAnswers([]);
        }
        setDataReceivedFlag(true);
        setCircularProgress(false)
      })
      .catch((err: any) => {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
        setCircularProgress(false)
        setQuestionnaire([]);
        setInitialAnswers([]);
        toast.error(Helper.getErrorText(err), Helper.bottom_center())
      });
  }

  const getAnswers = () => {
    if (currTabIndex <= 0)
      return answerArray
    else {
      const key = configItem.tabs?.find((i: any) => i.index === currTabIndex)?.title || ''
      let hisAnsObj = [] as any
      if (key) {
        Object.keys(hisAnswerArray[key])?.map((i: any) => {
          const keyObj = { [i]: hisAnswerArray[key][i] }
          hisAnsObj.push(keyObj)
        })
        return hisAnsObj
      }
    }
  }
  // Recursively collect default_answers for the branch revealed by `value`, following
  // nested defaults until the tree closes. Lets switching the top answer in-editor
  // propagate the specified defaults (INV-1) — e.g. choosing "No" auto-fills
  // negative_evidence — matching the backend open-preview. Only unanswered keys.
  const collectBranchDefaults = (q: any, value: any, haveKeys: Set<string>, acc: any[]) => {
    const matched = (q?.branches || []).find((b: any) => String(b.condition) === String(value))
    if (!matched?.questions) return acc
    matched.questions.forEach((sq: any) => {
      const da = sq?.default_answer
      if (sq?.id && !haveKeys.has(sq.id) && da !== undefined && da !== '') {
        acc.push({ [sq.id]: { value: da } })
        haveKeys.add(sq.id)
        collectBranchDefaults(sq, da, haveKeys, acc)
      }
    })
    return acc
  }

  const onTextChange = (obj: any, index: any, isParamText = false) => {
    if (currTabIndex !== 0)
      return

    const replaceIndex = answerArray.findIndex((ele: any) => Object.keys(ele)[0] === Object.keys(obj)[0])

    let newArray: any[]
    if (replaceIndex !== -1) {
      // Replace immutably — don't splice the original array
      newArray = [...answerArray]
      newArray[replaceIndex] = obj
      setAnswerArray(newArray)
      if (index?.branches) {
        deleteBranches(obj, index, '')   // functional removals compose on top of newArray
        // Auto-fill the newly-revealed branch's specified defaults (e.g. -> No fills
        // negative_evidence: presumed negative). Functional update composes after the
        // removals; never overwrites an already-answered key.
        const newVal = (Object.values(obj)[0] as any)?.value
        const haveKeys = new Set<string>(newArray.map((el: any) => Object.keys(el)[0]))
        const additions = collectBranchDefaults(index, newVal, haveKeys, [])
        if (additions.length) {
          newArray = [...newArray, ...additions]
          setAnswerArray((prev: any[]) => {
            const next = [...prev]
            additions.forEach((a: any) => {
              const k = Object.keys(a)[0]
              if (!next.some((el: any) => Object.keys(el)[0] === k)) next.push(a)
            })
            return next
          })
        }
      }

      if (isParamText)
        setAnswerArray((prev: any[]) => [...prev, obj])
    } else {
      newArray = [...answerArray, obj]
      setAnswerArray(newArray)
    }

    if (isAllQuestionnarie || data?.isBulkEdit) {
      const currPayload: any = {}
      newArray.forEach((item: any) => {
        const key = Object.keys(item)[0]
        currPayload[key] = Object.values(item)[0]
      })
      handleChange(qu, currPayload, false)
    }
  }

  const findKey = (array: any, key: any) => array.findIndex((ele: any) => ele === key);

  const isAnswerSelected = (
    answerKey: any,
    ans: any,
    component: any,
    scaleArray: any
  ) => {
    const answerArray = getAnswers()
    if (component === 'ScaleQuestion') {
      let currentIndex = 0
      if (
        answerArray &&
        answerArray.findIndex((ele: any) => findKey(Object.keys(ele), answerKey) !== -1) !== -1
      ) {
        let d =
          answerArray &&
          answerArray.map((ele: any, index: any) => {
            if (Object.keys(ele)[0] === answerKey) {
              currentIndex = index
              return ele[answerKey]['value']
            }
          })
        return {
          value: d[currentIndex],
          answered: true,
        }
      } else {
        let obj = {
          value: scaleArray[0]['value'],
          answered: false,
        }
        return obj
      }
    } else if (component === 'ParameterValue') {
      setDataReceivedFlag(false)
      var defaultValue: string = ''
      var defaultUnit: string = ''
      var defaultValueArray: any = []
      if (
        answerArray &&
        answerArray.findIndex(
          (ele: any) => findKey(Object.keys(ele), answerKey) !== -1
        ) !== -1
      ) {
        let d =
          answerArray &&
          answerArray.map((ele: any) => {
            if (Object.keys(ele)[0] === answerKey) {
              if (
                ele[answerKey]['value'] !== '' &&
                ele[answerKey]['units'] !== ''
              ) {
                defaultValue = ele[answerKey]['value']
                defaultUnit = ele[answerKey]['units']
                let obj = {
                  val: defaultValue,
                  uni: defaultUnit,
                }
                defaultValueArray.push(obj)
              }
            }
          })
        return defaultValueArray
      }
    } else if (component === 'InputText') {
      let currentIndex = 0
      if (
        answerArray &&
        answerArray.findIndex(
          (ele: any) => findKey(Object.keys(ele), answerKey) !== -1
        ) !== -1
      ) {
        let d =
          answerArray &&
          answerArray.map((ele: any, index: any) => {
            if (Object.keys(ele)[0] === answerKey) {
              currentIndex = index
              return ele[answerKey]['value']
            }
          })
        return d[currentIndex] || ''
      }
      return ''
    } else {
      return (
        answerKey && answerArray.findIndex(
          (ele: any) =>
            findKey(Object.keys(ele), answerKey) !== -1 &&
            ele[answerKey] &&
            ele[answerKey]['value'] === ans
        ) !== -1
      )
        ? true
        : false
    }
  }

  const deleteInnerBranch = (subBranch: any, type: string) => {
    return (subBranch.questions || []).map((obj: any) => {
      setAnswerArray((prev: any[]) => {
        const idx = prev.findIndex((obj1: any) => Object.keys(obj1).includes(obj.id))
        if (idx === -1) return prev
        const updated = [...prev]
        updated.splice(idx, 1)
        return updated
      })
      return deleteBranches('', obj, type)
    })
  }

  const deleteBranches = (selectedAnswer: any, index: any, type: string) => {
    if (index && !index.branches) {
      setAnswerArray((prev: any[]) => {
        const idx = prev.findIndex((obj1: any) => Object.keys(obj1).includes(index.id))
        if (idx === -1) return prev
        const updated = [...prev]
        updated.splice(idx, 1)
        return updated
      })
    } else {
      if (type !== 'inner') {
        let changedAnswer: any = Object.values(selectedAnswer)[0]
        let checkValue = index && index.branches && index.branches[0] && index.branches[0].condition
        if (changedAnswer !== checkValue) {
          index && index.branches && index.branches.forEach((subBranch: any) => {
            deleteInnerBranch(subBranch, 'inner')
          })
        }
      } else {
        index && index.branches && index.branches.forEach((subBranch: any) => deleteInnerBranch(subBranch, ''))
      }
    }
  }

  const isRecommended = (questionnaire_id: string) => {
    // Convert questionnaire_id to number for comparison
    const qIdNum = Number(questionnaire_id);
    const qIdStr = String(questionnaire_id);

    const result = recommended_calculators?.some((item: any) => {
      const itemId = item.questionnaire_id;
      // Compare both as numbers and as strings to handle type mismatches
      return itemId === qIdNum || itemId === qIdStr || String(itemId) === qIdStr;
    }) || false;

    return result;
  }

  const handleSubmit = (_type: string) => {
    setCircularProgress(true)
    handleIsSubmit(false)
    let payload: any = {};
    answerArray?.map((obj: any) => {
      let key: any = Object.keys(obj)[0]
      payload[key] = Object.values(obj)[0]
    })
    setAnswer(answer)

    patientService.updateQuestionnaires(patientId, {
      "data": [{
        "id": qu,
        "answer": payload,
        "is_recommended": isRecommended(qId),
        "confirmed": _type === 'confirm' ? true : false
      }]
    })
      .then((response: any) => {
        setCircularProgress(false)
        if (response?.data?.message === 'Success') {
          if (typeof handleIsSubmit === "function")
            handleIsSubmit(false)
          handleSave(_type, response?.data?.result[0] || {})
        } else {
          handleSave(_type, response?.data?.result[0] || {})
        }
      })
      .catch((res: any) => {
        handleIsSubmit(false)
        if (res?.response?.data?.error?.message)
          toast.error(Helper.getErrorText(res), Helper.bottom_center())
        else if (res?.response?.data?.result)
          handleSave('confirm_err', res?.response?.data?.result[0] || {})
        setCircularProgress(false)
      })
  }

  const getQuestionnarieSeverity = () => {
    let val = ''
    if (data.category === "diagnosis") {
      const firstQueId = question[0]?.id || ''
      if (firstQueId) {
        const currAns = currTabIndex === 0 ? answerArray : getAnswers();
        const firstQueRes = (currAns)?.find((i: any) => (Object.keys(i).includes(firstQueId)) && i)
        if ((firstQueRes && firstQueRes[firstQueId]?.value)) {
          val = firstQueRes[firstQueId]?.value || ''
          // val = (val === 'Yes') ? val : ''
        }
      }
      return val && val !== ''
        ? <Typography sx={{ fontSize: '0.7rem', fontWeight: '500', color: 'var(--color-text-primary)' }}>
          {data.name}:&nbsp;{val}
        </Typography>
        : ''
    }
    else {
      if (currTabIndex === 0 && data?.severity?.value) {
        val = `${data?.severity?.value}${data?.severity?.max_value ? `/${data?.severity?.max_value} ` : ''}`
      }
      else {
        const currAns = getAnswers();
        const currSeverity = (currAns)?.find((i: any) => (Object.keys(i).includes('severity')) && i)
        if ((currSeverity && currSeverity['severity']?.value)) {
          val = currSeverity['severity']?.value
          if (currSeverity['severity']?.max_value)
            val += `/${currSeverity['severity']?.max_value}`
        }
      }
      return <Typography sx={{ fontSize: '0.7rem', fontWeight: '500', color: 'var(--color-text-primary)' }}>
        Severity:&nbsp;{val || 'None'}
      </Typography>

    }
  }

  return (
    <Box className={classes.fullWidth} sx={{ overflow: 'hidden !important' }}>
      {!data?.isBulkEdit &&
        <DialogTitle id={`q - ${qId} `} classes={{ root: 'questionnarie-dialog' }} sx={{ paddingBottom: '0px !important' }}>
          <DialogTitleHeaderComponent title={data?.name} handleDialogClose={handleClose} />
        </DialogTitle>
      }
      {!data?.isBulkEdit &&
        <AppBar position="static" color="transparent" sx={{ boxShadow: 'none' }}>
          <Tabs value={currTabIndex} onChange={handleTabChange} variant="scrollable" sx={{ paddingTop: '0rem. 0.2rem !important' }}>
            {(configItem?.tabs)?.map((c: any, index: any) => (
              <Tooltip key={c.title} title={<span style={{ whiteSpace: 'pre-line' }}>{getTabTooltip(c)}</span>} arrow>
                <Tab label={(c.title).substring(0, 10)} {...a11yProps(index)} />
              </Tooltip>
            ))}
          </Tabs>
        </AppBar>
      }
      <DialogContent className={classes.questionnaireContainer}
        sx={isAllQuestionnarie ?
          { maxHeight: 'inherit', overflowY: 'hidden' }
          : { maxHeight: '25.5rem', overflowY: 'auto' }}
      >
        <Box className={classes.rationaleDialogHeader} mb={0.5} />
        <DialogContentText className={classes.zeroBottomMargin}>
          <CardContent key={'questionnaireCard'} className={classes.questionnaireCardContainer}>
            {showCircularProgress &&
              <Grid container direction="row" justifyContent="center" alignItems="center">
                <Grid item><CircularProgress size="2rem" /></Grid>
              </Grid>
            }
            {!showCircularProgress && !data?.isBulkEdit &&
              <Box pb={1} pl={0.2} pt={0.5}>
                {getQuestionnarieSeverity()}
              </Box>
            }
            {!showCircularProgress && question?.map((ques: any) => {
              return (
                <GenericQuestionnaireComponent
                  key={ques.id}
                  question={ques}
                  onTextChange={onTextChange}
                  isViewHistory={currTabIndex !== 0}
                  isAnswerSelected={isAnswerSelected}
                  answerSet={getAnswers()}
                  dataReceivedFlag={dataReceivedFlag}
                  type={''}
                  history={''}
                  location={''}
                  match={''}
                />
              )
            })
            }
          </CardContent>
        </DialogContentText>
      </DialogContent>
      {data.search_questionnaires || (!isAllQuestionnarie && !data?.isBulkEdit) && <>
        <Box className={classes.boxBorder} />
        <DialogActions className={classes.btnContainer}>
          <Button
            disabled={answerArray.length === 0 || showCircularProgress || currTabIndex !== 0}
            onClick={() => handleSubmit("confirm")}
            variant="outlined"
            size='small'
          >
            <strong>{`Save & Confirm`}</strong>
          </Button>
          <Button
            disabled={answerArray.length === 0 || showCircularProgress || currTabIndex !== 0}
            onClick={() => handleSubmit("save")}
            variant="outlined"
            size='small'
          >
            <strong>Save</strong>
          </Button>
          <Button disabled={showCircularProgress} onClick={handleClose} variant="outlined" size='small'>
            <strong>Cancel</strong>
          </Button>
        </DialogActions>
      </>
      }
    </Box>
  );
}

export default React.memo(withStyles(stylesheet)(QuestionnaireComponent));